import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeDeleteConfirm } from "../redux/slices/uiSlice.js";
import { IconAlertTriangle } from "@tabler/icons-react";
import { toast } from "react-toastify";
import Modal from "./ui/Modal";
import Button from "./ui/Button";
import { removePost } from "../redux/slices/postSlice.js";
import {
  removeComment,
  removeReply,
} from "../redux/slices/posts/commentSlice.js";
import { useAuth } from "../contexts/AuthContext";

const DeleteConfirmModal = () => {
  const dispatch = useDispatch();
  const { isOpen, itemId, itemType, parentId, postId } = useSelector(
    (state) => state.ui.showDeleteConfirm,
  );
  const { currentUser } = useAuth();

  if (!isOpen) return null;

  const getTitle = () => {
    switch (itemType) {
      case "post":
        return "Delete Post";
      case "comment":
        return "Delete Comment";
      case "reply":
        return "Delete Reply";
      default:
        return "Confirm Delete";
    }
  };

  const getMessage = () => {
    switch (itemType) {
      case "post":
        return "Are you sure you want to delete this post? This action cannot be undone.";
      case "comment":
        return "Are you sure you want to delete this comment? All replies to this comment will also be deleted.";
      case "reply":
        return "Are you sure you want to delete this reply?";
      default:
        return "Are you sure you want to delete this item? This action cannot be undone.";
    }
  };

  const handleDelete = async () => {
    if (!currentUser || !itemId) return;

    // Issue 1 Fix: Close modal immediately for optimistic UI
    // Redux handles optimistic hide, modal should not block
    dispatch(closeDeleteConfirm());
    toast.info(`Deleting ${itemType}...`, { autoClose: 2000 });

    try {
      switch (itemType) {
        case "post":
          await dispatch(
            removePost({
              postId: itemId,
              userId: currentUser.uid,
            }),
          ).unwrap();
          toast.success("Post deleted successfully");
          break;

        case "comment":
          await dispatch(
            removeComment({
              commentId: itemId,
            }),
          ).unwrap();
          toast.success("Comment deleted successfully");
          break;

        case "reply": {
          // Use parentId from Redux UI state for the parent comment id
          const commentId = parentId;
          if (!commentId) throw new Error("Could not find parent comment");

          await dispatch(
            removeReply({
              replyId: itemId,
              commentId,
              postId,
            }),
          ).unwrap();
          toast.success("Reply deleted successfully");
          break;
        }

        default:
          throw new Error("Unknown item type");
      }
    } catch (error) {
      console.error(`Error deleting ${itemType}:`, error);

      // Enhanced error messages with specific guidance
      let errorMsg = `Failed to delete ${itemType}`;
      const code = error?.code || "";
      const message = error?.message || "";

      if (code === "permission-denied" || message.includes("permission")) {
        errorMsg = `Cannot delete ${itemType}: You don't have permission. Only the author can delete their content.`;
      } else if (code === "not-found" || message.includes("not found")) {
        errorMsg = `This ${itemType} has already been deleted or doesn't exist.`;
      } else if (code === "network-error" || message.includes("network")) {
        errorMsg = `Network error while deleting ${itemType}. Please check your connection and try again.`;
      } else if (code === "unauthenticated" || message.includes("auth")) {
        errorMsg = `Please sign in to delete this ${itemType}.`;
      } else if (message) {
        errorMsg = `Failed to delete ${itemType}: ${message}`;
      }

      toast.error(errorMsg, { autoClose: 5000 });
      // Modal already closed, error toast shows failure
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => dispatch(closeDeleteConfirm())}
      size="sm"
      hideCloseButton={true}
    >
      <div className="flex flex-col items-center text-center px-2 py-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-inner bg-red-500 shadow-red-600/50">
          <IconAlertTriangle size={32} color="#ffffff" />
        </div>

        <h2 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">
          {getTitle()}
        </h2>
        <p className="text-sm text-gray-500 mb-8 leading-relaxed max-w-[280px]">
          {getMessage()}
        </p>

        <div className="flex w-full gap-3 mt-auto">
          <Button
            variant="secondary"
            onClick={() => dispatch(closeDeleteConfirm())}
            className="flex-1"
            size="md"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            className="flex-1"
            size="md"
          >
            Delete
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteConfirmModal;
